/* eslint-disable react-hooks/exhaustive-deps */
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { FilterMatchMode, FilterService } from 'primereact/api';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import DatatableActionColumn from './DatatableActionColumn';
import {
	useGetWorkforceByManagementCompanyQuery,
	useGetManagementCompanyInfoQuery
} from '../../../redux/managementCompanyWorkforce';
import {
	viewStateForWorkforceProfessionals,
	updateProfessionalsViewStateForManagementCompany
} from '../../../redux/flexWorkforceView';
import MultiSelectSearch from '../../../components/MultiSelectSearch/MultiSelectSearch';
import FilterUtils from '../../../utils/FilterUtils';
import LoadingSpinner from '../../../components/spinner/LoadingSpinner';
import IsAuthorizedForManagementCompany from '../../../components/IsAuthorizedForManagementCompany/IsAuthorizedForManagementCompany';
import { PERMISSION, PRIVATE_PATH } from '../../../constants';

import '../../../components/professionals/ProfessionalsList.scss';

const DEFAULT_SORT = { field: 'name', order: 1 };

const STATUSES = [
	{ label: 'Enabled', value: 'enabled', checked: true },
	{ label: 'Pending', value: 'pending', checked: true },
	{ label: 'Disabled', value: 'disabled', checked: true },
	{ label: 'Terminated', value: 'terminated', checked: true },
	{ label: 'Deactivated', value: 'deactivated', checked: true }
];

const DEFAULT_FILTER_OPTIONS = {
	statuses: STATUSES
};

const facilitiesCountTemplate = (item) => `${item.enabledFacilityCount} of ${item.facilityCount}`;

const isCgDisabled = (caregiver) => caregiver.status === 'disabled';
const isCgDeactivated = (caregiver) => !caregiver.isActive;

const mapCaregiversFlexData = (data) =>
	data?.map((caregiver) => {
		const {
			id,
			name,
			employee_status: employeeStatus,
			is_deactivated: isDeactivated,
			resource_types: resourceTypes,
			city,
			state,
			specialties,
			flex_active_facility_count: flexActiveFacilityCount,
			primary_employee_id_value: primaryEmployeeIdValue,
			enabled_facilities_count: enabledFacilityCount,
			phone_number: phoneNumber,
			email
		} = caregiver;

		const sortedResourceTypes = resourceTypes?.length ? [...resourceTypes].sort((a, b) => b.rank - a.rank) : null;
		const licenses = sortedResourceTypes?.map((type) => type.code).join(', ') || '-';

		const augmentedSpecialties = [];
		sortedResourceTypes?.forEach((type) => {
			const isCurrentResourceRT = ['CRT', 'RRT'].includes(type.code);

			const specialtiesForResourceType = specialties
				?.filter(
					(specialty) =>
						specialty.resource_type_id === type.id || (isCurrentResourceRT && specialty.code.startsWith('RT_'))
				)
				.sort((a, b) => a.short_name.localeCompare(b.short_name));
			if (specialtiesForResourceType?.length > 0) {
				augmentedSpecialties.push(
					`${isCurrentResourceRT ? 'RT' : type.code} - ${specialtiesForResourceType
						.map((spec) => `${spec.short_name}`)
						.join(', ')}`
				);
			}
		});

		const uniqueSpecialties = [...new Set(augmentedSpecialties)];
		const specialtiesString = uniqueSpecialties?.length ? uniqueSpecialties.join('; ') : '-';

		let status = employeeStatus;
		if (isDeactivated) {
			status = 'Deactivated';
		}

		return {
			id,
			name,
			status,
			employeeStatus,
			isActive: !isDeactivated,
			enabledFacilityCount,
			facilityCount: flexActiveFacilityCount,
			enabledLocationsCountText: `${enabledFacilityCount} of ${flexActiveFacilityCount}`,
			licenses,
			specialties: specialtiesString,
			specialtiesString,
			state,
			city,
			primaryEmployeeIdValue,
			phoneNumber,
			email
		};
	});

const buildFilterOptionsFromFilters = (filters) => {
	const persisted = filters?.status?.value;
	if (!Array.isArray(persisted)) return DEFAULT_FILTER_OPTIONS;
	return { statuses: STATUSES.map((s) => ({ ...s, checked: persisted.includes(s.value) })) };
};

const buildMultiselectFiltersFromFilters = (filters) => {
	const persisted = filters?.status?.value;
	return { statuses: Array.isArray(persisted) ? persisted : STATUSES.map((s) => s.value) };
};

const FlexProfessionalsList = ({ managementCompany, dataTableRef, exportFilename, filters, setFilters }) => {
	const dispatch = useDispatch();
	const history = useHistory();
	const filterOptions = useMemo(() => buildFilterOptionsFromFilters(filters), [filters]);
	const multiselectFilters = useMemo(() => buildMultiselectFiltersFromFilters(filters), [filters]);

	const viewState = useSelector(viewStateForWorkforceProfessionals(managementCompany.id));
	const sort = viewState?.sort || DEFAULT_SORT;
	const persistedScrollTop = viewState?.scrollTop;

	const handleAddProfessional = () => {
		history.push(`${PRIVATE_PATH.WORKFORCE_PAGE}/${managementCompany.id}/professionals/create`);
	};

	const onSort = (e) => {
		dispatch(
			updateProfessionalsViewStateForManagementCompany({
				managementCompanyId: managementCompany.id,
				sort: { field: e.sortField, order: e.sortOrder }
			})
		);
	};

	const { data: caregivers, isLoading } = useGetWorkforceByManagementCompanyQuery(managementCompany.id, {
		refetchOnMountOrArgChange: true
	});
	const { data: managementCompanyInfo, isLoading: isMcInfoLoading } = useGetManagementCompanyInfoQuery(
		managementCompany.id,
		{
			refetchOnMountOrArgChange: true
		}
	);

	let emptySearchMessage = '';
	if (caregivers && caregivers.length > 0) {
		emptySearchMessage = 'No professionals found. Clear or refine your search to see professionals.';
	}

	const handleMultiSelectChange = (_options) => {
		const { updatedFilters } = FilterUtils.handleMultiSelectChange(
			_options,
			'statuses',
			filterOptions,
			multiselectFilters
		);
		setFilters((prev) => {
			return {
				...prev,
				status: { ...(prev.status || {}), value: updatedFilters.statuses, matchMode: FilterMatchMode.CUSTOM }
			};
		});
	};

	// eslint-disable-next-line react/no-unstable-nested-components
	const ToolbarItems = () => (
		<div className="right-align-dropdown">
			<MultiSelectSearch
				showLongSelectionText
				className="select-filter-status-container"
				label="Status"
				options={filterOptions.statuses}
				onApply={handleMultiSelectChange}
			/>
			<IsAuthorizedForManagementCompany
				exactPermission={PERMISSION.PORTAL_ADD_FLEX_PROFESSIONAL}
				managementCompanyId={managementCompany.id}
			>
				<div className="vertical-ruler" />
				<Button className="btn-add-professional flex-workforce" onClick={handleAddProfessional}>
					+ Add professional
				</Button>
			</IsAuthorizedForManagementCompany>
		</div>
	);

	const professionals = caregivers ? mapCaregiversFlexData(caregivers) : [];
	const { management_company_primary_employee_id_name: managementCompanyPrimaryId } = managementCompanyInfo || {};

	const [filteredProfessionalsCount, setFilteredProfessionalsCount] = useState(null);

	FilterService.register(`custom_status`, (item, filter) => {
		return filter.indexOf(item.toLowerCase()) > -1;
	});

	const statusTemplate = useCallback((item) => {
		let cellClasses = item.status === 'disabled' ? 'column-status disabled' : 'column-status';
		if (item.status === 'pending' || item.status.includes('app invite sent')) {
			cellClasses += ' pending-column-status';
		} else if (item.status === 'terminated') {
			cellClasses += ' status-column-terminated';
		}
		return <span className={cellClasses}>{item.status}</span>;
	}, []);

	const specialtyTemplate = useCallback((item) => {
		const hasSpecialties = !!item.specialties.length;
		const isDeactivated = isCgDeactivated(item);
		return (
			<span
				style={{
					textDecoration: hasSpecialties && isDeactivated ? 'line-through' : 'none'
				}}
			>
				{item.specialtiesString}
			</span>
		);
	}, []);

	const professionalTemplate = (caregiver) => (
		<DatatableActionColumn managementCompany={managementCompany} caregiver={caregiver} />
	);

	const defaultTemplate = useCallback((item, columnData) => {
		const { field } = columnData;
		return item[field] ? item[field] : '-';
	}, []);

	const employeeIdTemplate = useCallback((item, columnData) => <pre>{defaultTemplate(item, columnData)}</pre>, []);

	const rowClass = useCallback((item) => {
		return {
			'row-disabled': isCgDisabled(item),
			'row-deactivated': isCgDeactivated(item),
			'row-terminated': item.status === 'terminated'
		};
	}, []);

	const cellClass = useCallback((item, columnData) => {
		return {
			'table-cell': true,
			'no-line-through': ['status', 'actions'].includes(columnData.field)
		};
	}, []);

	const onFilter = (e) => setFilteredProfessionalsCount(e.length);

	// PrimeReact's DataTable only fires onValueChange on filter changes, not on value changes, and
	// skips the first mount via useUpdateEffect. Without this, the count is stale on initial mount
	// with persisted filters and after status mutations invalidate the cache. Bump the filters ref
	// whenever caregivers data updates so the DataTable re-processes and reports the current count.
	// Guard with a ref because the parent's setFilters identity changes every render.
	const lastCaregiversRef = useRef(null);
	useEffect(() => {
		if (!caregivers || lastCaregiversRef.current === caregivers) return;
		lastCaregiversRef.current = caregivers;
		if (caregivers.length === 0) {
			setFilteredProfessionalsCount(0);
			return;
		}
		setFilters((prev) => ({ ...prev }));
	}, [caregivers, setFilters]);

	useEffect(() => {
		lastCaregiversRef.current = null;
		setFilteredProfessionalsCount(null);
	}, [managementCompany.id]);

	const _filteredProfessionalsCount =
		filteredProfessionalsCount !== null ? filteredProfessionalsCount : professionals.length;

	const restoredForMcRef = useRef(null);
	const latestScrollTopRef = useRef(null);
	const scrollTopMcIdRef = useRef(managementCompany.id);

	// Persist the scroll position to redux when the user leaves this MC's view — either by
	// switching management companies (detected via previous-id ref) or by unmounting the
	// datatable (cleanup runs on unmount). scrollTopMcIdRef always holds the mcId that
	// latestScrollTopRef's value belongs to.
	useEffect(() => {
		if (scrollTopMcIdRef.current !== managementCompany.id && latestScrollTopRef.current != null) {
			dispatch(
				updateProfessionalsViewStateForManagementCompany({
					managementCompanyId: scrollTopMcIdRef.current,
					scrollTop: latestScrollTopRef.current
				})
			);
			latestScrollTopRef.current = null;
		}
		scrollTopMcIdRef.current = managementCompany.id;
		return () => {
			if (latestScrollTopRef.current == null) return;
			dispatch(
				updateProfessionalsViewStateForManagementCompany({
					managementCompanyId: scrollTopMcIdRef.current,
					scrollTop: latestScrollTopRef.current
				})
			);
			latestScrollTopRef.current = null;
		};
	}, [managementCompany.id, dispatch]);

	useEffect(() => {
		if (restoredForMcRef.current === managementCompany.id) return undefined;
		if (isLoading || professionals.length === 0) return undefined;
		restoredForMcRef.current = managementCompany.id;
		if (!persistedScrollTop) return undefined;
		// only try to scroll on the next paint cycle, when we know the datatable has been already rendered
		const rafId = requestAnimationFrame(() => {
			dataTableRef.current?.getVirtualScroller()?.scrollTo({ top: persistedScrollTop, behavior: 'auto' });
		});
		return () => cancelAnimationFrame(rafId);
	}, [isLoading, professionals.length, managementCompany.id, persistedScrollTop, dataTableRef]);

	return (
		<div className="professionals-list">
			<div className="toolbar">
				<p className="total-count">
					{_filteredProfessionalsCount} Professional{_filteredProfessionalsCount > 1 ? 's' : ''}
				</p>
				<div className="flex-one" />
				<ToolbarItems />
			</div>
			<div className="datatable-wrapper">
				<div className="wrapper-container">
					<DataTable
						ref={dataTableRef}
						value={professionals}
						scrollable
						scrollHeight="flex"
						removableSort
						sortField={sort.field}
						sortOrder={sort.order}
						onSort={onSort}
						dataKey="id"
						virtualScrollerOptions={{
							itemSize: 65,
							onScroll: (e) => {
								// store the current position in a ref on every scroll
								// we don't need it in redux until the user goes away from the page
								if (restoredForMcRef.current !== managementCompany.id) return;
								latestScrollTopRef.current = e.target.scrollTop;
							}
						}}
						tableStyle={{ minWidth: '100rem' }}
						size="small"
						stripedRows
						cellClassName={cellClass}
						rowClassName={rowClass}
						loading={isLoading || isMcInfoLoading}
						filters={filters}
						globalFilterFields={[
							'name',
							'status',
							'licenses',
							'specialties',
							'specialtiesString',
							'email',
							'phoneNumber',
							'enabledLocationsCountText',
							'primaryEmployeeIdValue'
						]}
						emptyMessage={emptySearchMessage}
						loadingIcon={<LoadingSpinner />}
						exportFilename={exportFilename}
						onValueChange={onFilter}
					>
						<Column field="name" header="Professional" style={{ width: '15%' }} sortable body={defaultTemplate} />
						{managementCompanyPrimaryId && (
							<Column
								field="primaryEmployeeIdValue"
								filterField="primaryEmployeeIdValue"
								header={managementCompanyPrimaryId}
								style={{ width: '10%' }}
								sortable
								body={employeeIdTemplate}
							/>
						)}
						<Column field="status" header="Status" style={{ width: '8%' }} sortable body={statusTemplate} />
						<Column field="licenses" header="License type" style={{ width: '8%' }} sortable />
						<Column field="specialties" header="Specialty" style={{ width: '17%' }} sortable body={specialtyTemplate} />
						<Column field="email" header="Email" style={{ width: '12%' }} sortable body={defaultTemplate} />
						<Column field="phoneNumber" header="Phone number" style={{ width: '7%' }} sortable body={defaultTemplate} />
						{/* We keep the city and state columns with display: none to keep them in the CSV export but not shown in the table */}
						<Column
							field="city"
							header="City"
							style={{ width: '15%', display: 'none' }}
							sortable
							body={defaultTemplate}
						/>
						<Column
							field="state"
							header="State"
							style={{ width: '4%', display: 'none' }}
							sortable
							body={defaultTemplate}
						/>
						<Column
							key="locations"
							header="Locations"
							field="enabledLocationsCountText"
							filterField="enabledLocationsCountText"
							sortable
							sortField="enabledFacilityCount"
							body={facilitiesCountTemplate}
							style={{ width: '4%' }}
						/>
						<Column
							field="actions"
							header="Actions"
							style={{ width: '4%' }}
							alignFrozen="right"
							exportable={false}
							body={professionalTemplate}
						/>
					</DataTable>
				</div>
			</div>
		</div>
	);
};

export default memo(FlexProfessionalsList);
